import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import BylineWithHeadshot from '@components/BylineWithHeadshot';
import { RailsEnv, useAppContext } from '@contexts/AppContext';
import { Author } from '@interfaces/authors';
import { IMGIX_URL } from '@src/env';

import OrgChartSearch from './OrgChartSearch/OrgChartSearch';
import { OrgChartCompanyHeaderProps } from './interfaces';

const DEFAULT_AVATAR = 'https://tii.imgix.net/production/org_charts/default-avatar.png';

const OrgChartCompanyHeader = ({
  authors,
  description,
  lastUpdated,
  slug,
  title,
}: OrgChartCompanyHeaderProps) => {
  const { env } = useAppContext();
  const railsEnv = env.RAILS_ENV || RailsEnv.Development;

  const formatDateIfNeeded = (dateString) => {
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/; // some of our org charts have this regex.

    if (dateFormatRegex.test(dateString)) {
      // It's in YYYY-MM-DD format, convert it
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }

    return dateString;
  };

  const authorsWithCleanedPictures: Author[] = authors.map((author) => {
    const original = author.picture;
    let cleanPicture = '';
    if (!original) {
      cleanPicture = DEFAULT_AVATAR;
    } else {
      cleanPicture = `${IMGIX_URL}/${railsEnv}/avatars/${author.id}/${original}`;
    }
    return {
      ...author,
      picture: cleanPicture,
    };
  });

  return (
    <div className="mx-auto mb-32 flex max-w-container-lg flex-col">
      <p className="m-0 font-suisse-intl-mono text-overline-md text-primary-magenta">Org Charts</p>
      <h1 className="mb-0 mt-8 hyphens-auto break-words text-headline-alt-lg !leading-[1.1] md:text-headline-alt-xl">
        {title}
      </h1>
      {description && (
        <p className="font-suisse-neue text-subheadline-sm">{ReactHtmlParser(description)}</p>
      )}
      <div>
        {authors.length > 0 && <BylineWithHeadshot authors={authorsWithCleanedPictures} />}
        <time>
          <p className="mb-0 mt-8 text-caption-md italic">
            Last Updated {formatDateIfNeeded(lastUpdated)}
          </p>
        </time>
      </div>
      <div className="mt-24 flex justify-between">
        <div className="flex flex-row items-center justify-start gap-12">
          <OrgChartSearch maxResults={8} />
          <a
            href={`/org-charts/${slug}/csv-download`}
            className="m-0 font-suisse-intl-mono text-overline-sm text-primary-magenta hover:cursor-pointer dark:text-primary-pink"
          >
            Download CSV
          </a>
        </div>
      </div>
    </div>
  );
};

export default OrgChartCompanyHeader;
